import Text from '@shared/components/content/text';
import Logo from '@shared/components/logo/logo';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { cn, formatDate } from '@shared/lib/utils';
import { ReportContext, ReportResult, ReportType } from '../types';

interface ReportIntroProps {
  result: ReportContext<ReportResult>['result'];
  downloadMode: boolean;
  locationHash?: string;
  children?: React.ReactNode;
  tags?: string[];
  reportType: ReportType;
}

export default function ReportIntro({
  reportType,
  result,
  downloadMode,
  locationHash,
  children,
  tags = [],
}: ReportIntroProps) {
  const ccf = reportType === 'ccf';
  return (
    <div
      className={cn('flex items-center bg-primary h-screen', {
        ' h-[--page-height] w-[--page-width]': downloadMode,
      })}
    >
      <div className="space-y-32 px-20">
        {downloadMode ? (
          <Logo variant="name-white-png" className="w-[6cm]" />
        ) : (
          <Logo variant="name" color="light" className="w-80" />
        )}
        <div className="space-y-4">
          <Text variant="h1" className="font-heading text-4xl text-gray-00">
            {result?.organization.name}
          </Text>
          <Text
            variant="h1"
            className="font-heading text-4xl text-teal-400"
            data-testid="report-title"
          >
            {result?.title}
          </Text>
          {tags.length > 0 && (
            <div className="ml-1 mt-2 flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <Badge key={tag} className="text-nowrap">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
          {ccf && (
            <Text className="text-md text-gray-00">
              {formatDate(result?.startDate as string, 'do MMMM yyyy')} -
              {formatDate(result?.endDate as string, 'do MMMM yyyy')}
            </Text>
          )}
        </div>
        {!downloadMode && (
          <Button
            variant="support"
            size="lg"
            className="border-none bg-warning-500 text-primary"
            onClick={() => {
              window.location.hash = locationHash || '#introduction';
            }}
            data-testid="view-report-button"
          >
            View report
          </Button>
        )}
        {ccf && (
          <div>
            <img
              src={
                new URL(
                  `@shared/assets/client-assets/ghg-logo.png`,
                  import.meta.url
                ).href
              }
              className="w-48"
              alt="greenhouse gas protocol logo"
            />
          </div>
        )}
      </div>
    </div>
  );
}
